import type { AssessmentOverview, SummaryCards as SummaryCardsData } from '@assessments/shared'
import { SectionCompletionState } from '@assessments/shared'
import { Card } from '@/components/ui/card'
import { formatDeadline } from '@/lib/formatDeadline'

function SummaryCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Card className="px-5 py-4">
      <div className="mb-2 text-[11px] font-semibold tracking-[0.12em] text-subtle uppercase">
        {label}
      </div>
      {children}
    </Card>
  )
}

/** Big figure with a quieter denominator, e.g. "3 / 9 sections". */
function Ratio({ value, of }: { value: number; of: string }) {
  return (
    <div className="font-jakarta text-[26px] leading-none font-bold text-heading">
      {value}
      <span className="text-[17px] font-medium text-subtle"> / {of}</span>
    </div>
  )
}

export interface SummaryCardsProps {
  summary: SummaryCardsData
  /** Section lists, so the cards can report section and question totals. */
  overview: Pick<AssessmentOverview, 'requiredSections' | 'optionalSections'>
  /** Titles of prerequisites still holding up submission, named beside the blocker. */
  outstandingPrerequisites?: string[]
}

export function SummaryCards({
  summary,
  overview,
  outstandingPrerequisites = [],
}: SummaryCardsProps) {
  const { requiredCompleteCount, requiredTotalCount, deadline, applicationOwner } = summary

  const sections = [...overview.requiredSections, ...overview.optionalSections]
  const sectionsComplete = sections.filter(
    (s) => s.state === SectionCompletionState.Complete,
  ).length
  const answered = sections.reduce((n, s) => n + s.answeredCount, 0)
  const questions = sections.reduce((n, s) => n + s.questionCount, 0)

  const pastDue = deadline.state === 'past_due'
  const requiredOutstanding = requiredCompleteCount < requiredTotalCount

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <SummaryCard label="Overall progress">
        <Ratio value={sectionsComplete} of={`${sections.length} sections`} />
        <div className="mt-2 text-[15px] leading-snug text-body">
          {answered} of {questions} questions answered
        </div>
      </SummaryCard>

      <SummaryCard label="Required">
        <Ratio value={requiredCompleteCount} of={`${requiredTotalCount} complete`} />
        {requiredOutstanding && (
          <>
            <div className="mt-2 text-[15px] leading-snug text-danger">
              All required sections must be complete to submit
            </div>
            {outstandingPrerequisites.length > 0 && (
              <div className="mt-1 text-[15px] leading-snug text-subtle">
                Including {outstandingPrerequisites.join(', ')}
              </div>
            )}
          </>
        )}
      </SummaryCard>

      <SummaryCard label="Deadline">
        <div className="font-jakarta text-[21px] leading-tight font-bold text-heading">
          {formatDeadline(deadline)}
        </div>
        {pastDue && <div className="mt-2 text-[15px] text-danger">Past due</div>}
      </SummaryCard>

      <SummaryCard label="Application owner">
        {applicationOwner ? (
          <>
            <div className="font-jakarta text-[21px] leading-tight font-bold text-heading">
              {applicationOwner.name}
            </div>
            {applicationOwner.email && (
              <div className="mt-1 text-[15px] break-all text-body">{applicationOwner.email}</div>
            )}
          </>
        ) : (
          <div className="text-[15px] text-subtle">Not assigned</div>
        )}
      </SummaryCard>
    </div>
  )
}
